import {
  Box,
  Center,
  Divider,
  Grid,
  GridItem,
  HStack,
  Spinner,
  useTheme,
  VStack,
} from '@chakra-ui/react';
import type { ChakraTheme } from '@duke-hq/ui/theme/chakraTheme';
import FiltersProvider from '@duke-hq/filters/context/FiltersProvider';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import { useFlag } from '@duke-hq/flags/react';
import Header from './components/Header';
import SidePanel from './components/side-panel/SidePanel';
import DueForCompletionSection from './components/sections/DueForCompletion';
import EnrolmentHealthSection from './components/sections/EnrolmentHealth';
import WorkPlacementsSection from './components/sections/WorkPlacements';
import ParticipantActivitySection from './components/sections/ParticipantActivity';
import RevisionsSection from './components/sections/Revisions';
import ProgressSection from './components/sections/Progress';
import DashboardProvider from './context/Provider';
import useDashboardContext from './context/useDashboardContext';

function DashboardContent(): React.ReactElement {
  const theme = useTheme<ChakraTheme>();
  const { filtersLoaded } = useFiltersContext();
  const { PanelContentComponent } = useDashboardContext();
  const workPlacementEnabled = useFlag('WORK_PLACEMENT_FEATURE');

  if (!filtersLoaded) {
    return (
      <Center w="full" h="full">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Box w="full" h="full" overflow="hidden" bg="bg.default">
      <Header />
      <HStack w="full" h="full" alignItems="flex-start" gap="0">
        <Box
          flex="1"
          overflowY="auto"
          pb="5"
          height={`calc(100vh - ${theme.components.Dashboard.sizes.mainContentTopOffset})`}
        >
          <VStack py="4" px="5" pt="1" gap="5" w="full" alignItems="flex-start">
            <VStack pt="5" pb="2" w="full" alignItems="flex-start">
              <DueForCompletionSection />
            </VStack>
            <Divider orientation="horizontal" size="1px" color="border.default" />
            <Grid w="full" templateColumns="repeat(6, 1fr)" gap="4">
              <GridItem colSpan={{ base: 6, md: 4, xl: 4 }}>
                <EnrolmentHealthSection />
              </GridItem>
              <GridItem colSpan={{ base: 6, md: 2, xl: 2 }}>
                <RevisionsSection />
              </GridItem>
            </Grid>
            <ParticipantActivitySection />
            {workPlacementEnabled && <WorkPlacementsSection />}
            <ProgressSection />
          </VStack>
        </Box>
        {PanelContentComponent !== null && <SidePanel PanelContent={PanelContentComponent} />}
      </HStack>
    </Box>
  );
}

export function Dashboard(): React.ReactElement {
  return (
    <DashboardProvider>
      <FiltersProvider localStorageKey="dashboard_page_filters">
        <DashboardContent />
      </FiltersProvider>
    </DashboardProvider>
  );
}
